/**
 * Mobile device detection for trading restrictions
 *
 * This utility detects mobile phones to disable trading functionality
 * while keeping market data (charts, quotes) fully functional.
 *
 * Scope: Phones only. Tablets, foldable phones (unfolded), and desktops retain full trading.
 */

/**
 * Detect if the current device is a mobile phone (not tablet or foldable)
 * Fail-safe: returns false if detection fails (allows trading)
 *
 * Detection logic:
 * - Uses window.innerWidth/innerHeight to detect current viewport size
 *   (responds to foldable phone fold/unfold state changes)
 * - Min viewport dimension < 600px = phone (blocked)
 * - Min viewport dimension >= 600px = tablet/foldable/desktop (allowed)
 * - Also requires touch capability to distinguish from small desktop windows
 *
 * Device classification:
 * - Regular phones (360-480px min dim) → blocked
 * - Foldable phones unfolded (~690px min dim) → allowed
 * - Foldable phones folded (~344px min dim) → blocked (screen too small)
 * - Tablets (768px+ min dim) → allowed
 * - Desktops (no touch) → allowed
 *
 * @returns {boolean} true if mobile phone, false otherwise
 */
export function isMobilePhone() {
  if (typeof window === "undefined") {
    return false;
  }

  // Use window.innerWidth/innerHeight for dynamic viewport detection
  // This updates when foldable phones switch between folded/unfolded states
  // Math.min handles both portrait and landscape orientations
  const minDim = Math.min(window.innerWidth, window.innerHeight);
  const hasTouch = "ontouchstart" in window || navigator.maxTouchPoints > 0;
  const isMobile = minDim < 600 && hasTouch;

  return isMobile;
}

/**
 * Combined check: replay mode OR mobile phone
 * Use this function for all trading-related blocking decisions
 *
 * @returns {boolean} true if trading should be disabled
 */
export function isTradingDisabled() {
  // Check replay mode first (existing functionality)
  if (typeof window !== "undefined" && window.__REPLAY_MODE_ACTIVE__) {
    return true;
  }

  // Then check mobile phone
  return isMobilePhone();
}

/**
 * Get the reason why trading is disabled (for user messages)
 *
 * @returns {string|null} reason string or null if trading is enabled
 */
export function getTradingDisabledReason() {
  if (typeof window !== "undefined" && window.__REPLAY_MODE_ACTIVE__) {
    return "replay";
  }

  if (isMobilePhone()) {
    return "mobile";
  }

  return null;
}
